/**
 * Thin invoke wrappers around the tauri-plugin-voice `dev_*` commands.
 *
 * Returns raw data from the native side — the UI owns formatting and
 * loading state. On desktop/web these resolve to empty/zero payloads so
 * the dev surface renders during browser development without blowing up.
 */
import { invoke } from "@tauri-apps/api/core";

import type {
  DevAdaptationEvent,
  DevExportResult,
  DevResetResult,
  DevSessionSummary,
  DevStorageStats,
  DevUnknownVoice,
  DevUnknownVoiceOccurrence,
  DevUnknownVoiceTurnGroup,
  DevVoiceprint,
  DevVoiceprintSample,
} from "@/types/devMode";
import type { ProcessedInteraction } from "@/types/voice";

const isNative =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

export async function devListVoiceprints(): Promise<DevVoiceprint[]> {
  if (!isNative) return [];
  return invoke<DevVoiceprint[]>("plugin:voice|dev_list_voiceprints");
}

export async function devListSamples(
  voiceprintId: string
): Promise<DevVoiceprintSample[]> {
  if (!isNative) return [];
  return invoke<DevVoiceprintSample[]>("plugin:voice|dev_list_samples", {
    voiceprintId,
  });
}

export async function devListAdaptations(
  limit = 200
): Promise<DevAdaptationEvent[]> {
  if (!isNative) return [];
  return invoke<DevAdaptationEvent[]>("plugin:voice|dev_list_adaptations", {
    limit,
  });
}

export async function devListSessions(
  limit = 20
): Promise<DevSessionSummary[]> {
  if (!isNative) return [];
  return invoke<DevSessionSummary[]>("plugin:voice|dev_list_sessions", {
    limit,
  });
}

/**
 * Phase 3u — every voice the cross-session catalog has silently
 * remembered, whether or not it ever surfaced as a prompt.
 */
export async function devListUnknownVoices(): Promise<DevUnknownVoice[]> {
  if (!isNative) return [];
  return invoke<DevUnknownVoice[]>("plugin:voice|dev_list_unknown_voices");
}

export async function devListUnknownVoiceOccurrences(
  unknownVoiceId: string
): Promise<DevUnknownVoiceOccurrence[]> {
  if (!isNative) return [];
  return invoke<DevUnknownVoiceOccurrence[]>(
    "plugin:voice|dev_list_unknown_voice_occurrences",
    { unknownVoiceId }
  );
}

/**
 * Phase 3u — full conversation history this catalog entry
 * contributed to, grouped by session. Used by the Catalog detail
 * sheet so the user can read what an unresolved voice actually
 * said even when they skipped the post-session prompt.
 */
export async function devListUnknownVoiceTurns(
  unknownVoiceId: string
): Promise<DevUnknownVoiceTurnGroup[]> {
  if (!isNative) return [];
  return invoke<DevUnknownVoiceTurnGroup[]>(
    "plugin:voice|dev_list_unknown_voice_turns",
    { unknownVoiceId }
  );
}

/**
 * Phase 3u — name a catalogued voice. Retroactively attributes
 * every turn this voice owned across every session to the chosen
 * contact, creates interactions per session, and queues background
 * summary generation.
 */
export async function resolveUnknownVoice(
  unknownVoiceId: string,
  contactId: string
): Promise<ProcessedInteraction[]> {
  if (!isNative) return [];
  return invoke<ProcessedInteraction[]>(
    "plugin:voice|resolve_unknown_voice",
    { unknownVoiceId, contactId }
  );
}

/**
 * "This is me" on a cataloged voice. Merges every turn this voice
 * owned across every session into user_self, folds the catalog
 * centroid as a new sample on the user_self voiceprint, and marks
 * the catalog row resolved to user_self so it stops surfacing as
 * actionable. Returns the new user_self sample count + the observed
 * cosine so the UI can surface how confident the merge was.
 */
export async function adaptUserSelfFromSpeaker(
  unknownVoiceId: string
): Promise<{ sampleCount: number; cosine: number }> {
  if (!isNative) return { sampleCount: 0, cosine: 0 };
  return invoke<{ sampleCount: number; cosine: number }>(
    "plugin:voice|adapt_user_self_from_speaker",
    { unknownVoiceId }
  );
}

/**
 * Phase 3u — regenerate Foundation Models summaries for every
 * interaction currently missing one. Heals past batch resolves where
 * concurrent LanguageModelSession calls collided. Returns the count
 * queued; processing runs in the background.
 */
export async function devBackfillSummaries(): Promise<{ queued: number }> {
  if (!isNative) return { queued: 0 };
  return invoke<{ queued: number }>("plugin:voice|dev_backfill_summaries");
}

export async function devGetStorageStats(): Promise<DevStorageStats> {
  if (!isNative) {
    return {
      dbSizeBytes: 0,
      sessionsDirSizeBytes: 0,
      samplesDirSizeBytes: 0,
      rawDirSizeBytes: 0,
      voiceprintCount: 0,
      sampleCount: 0,
      sessionCount: 0,
      turnCount: 0,
      interactionCount: 0,
      provisionalCount: 0,
      adaptationCount: 0,
    };
  }
  return invoke<DevStorageStats>("plugin:voice|dev_get_storage_stats");
}

export async function devExportData(): Promise<DevExportResult> {
  if (!isNative) return { path: "n/a", sizeBytes: 0 };
  return invoke<DevExportResult>("plugin:voice|dev_export_data");
}

export async function devWipeVoiceData(): Promise<void> {
  if (!isNative) return;
  await invoke("plugin:voice|dev_wipe_voice_data");
}

/**
 * Phase 3r-3 recovery — roll a voiceprint back to its pre-adaptation
 * enrollment centroid. Typically invoked on `user_self` when a prior
 * "This was me" tap on a mixed/contaminated cluster adapted the
 * anchor too broadly. After reset, the voiceprint returns to
 * sample_count=1 and trust=0.3 and must re-earn auto-match
 * eligibility through normal adaptation.
 */
export async function devResetVoiceprintToEnrollment(
  id: string
): Promise<DevResetResult> {
  if (!isNative) return { id, sampleCount: 1, trustScore: 0.3 };
  return invoke<DevResetResult>(
    "plugin:voice|dev_reset_voiceprint_to_enrollment",
    { id }
  );
}
